import { platform, type Platform } from '@floating-ui/dom'
import { reducedMotion$ } from '@schmancy/directives/reduced-motion'
import { DURATION_EMPHASIZED_DECELERATE, EASE_EMPHASIZED_DECELERATE } from '@schmancy/utils/animation'

/**
 * An anchored surface is attached to an element, and a soft keyboard moves no
 * element: Android Chrome's default `resizes-visual` and iOS Safari shrink only
 * the visual viewport. So an anchored surface authors no keyboard-driven motion
 * at all. Revealing a covered focused field is the platform's job: the browser
 * pans the visible area onto it, and the surface moves with the page.
 */

/**
 * Floating UI's DOM platform, clipping against the LAYOUT viewport rather than
 * the visual one.
 *
 * The stock `getViewportRect` is sized from `visualViewport`, and Chrome fires
 * window `resize` when the keyboard shows even though `innerHeight` holds — so
 * every `autoUpdate` loop re-ran and `flip`/`shift`/`size` re-clamped the
 * surface into the strip above the keyboard, detaching it from its trigger in
 * one frame. A pinch-zoom shrinks the same box and dragged surfaces the same
 * way. Against the layout viewport those changes leave nothing to answer, while
 * a real resize still changes `clientWidth`/`clientHeight` and re-places.
 *
 * Only the root boundary is swapped: clipping ancestors and a caller's own
 * `boundary` still clip exactly as before.
 *
 * `@floating-ui/dom` 1.8 ships this as `rootBoundary: 'layoutViewport'`; the
 * `^1.7.6` range still admits 1.7.6, which has no such root, so this override
 * stays until the floor is 1.8 and the three call sites can pass that option.
 */
export const layoutViewportPlatform: Platform = {
	...platform,
	async getClippingRect(args) {
		const doc = args.element.ownerDocument
		const vv = doc.defaultView?.visualViewport
		if (args.rootBoundary !== 'viewport' || !vv) return platform.getClippingRect.call(this, args)
		const [visible, ancestors] = await Promise.all([
			platform.getClippingRect.call(this, { ...args, boundary: [], rootBoundary: 'viewport' }),
			platform.getClippingRect.call(this, { ...args, rootBoundary: 'document' }),
		])
		// In every coordinate space Floating UI works in, the visual viewport sits
		// at (offsetLeft, offsetTop) inside the layout viewport and differs from it
		// only in size — so the layout box is the visible one with that offset and
		// that size difference taken back, scrollbar corrections included.
		const left = visible.x - vv.offsetLeft
		const top = visible.y - vv.offsetTop
		const right = left + visible.width + doc.documentElement.clientWidth - vv.width
		const bottom = top + visible.height + doc.documentElement.clientHeight - vv.height
		const x = Math.max(left, ancestors.x)
		const y = Math.max(top, ancestors.y)
		return {
			x,
			y,
			width: Math.min(right, ancestors.x + ancestors.width) - x,
			height: Math.min(bottom, ancestors.y + ancestors.height) - y,
		}
	},
}

const GLIDE_ID = 'schmancy-glide-y'

/**
 * Moves `el` vertically to `toPx` on the house decelerate-to-rest token, from
 * `fromPx` — a position stated relative to where `el` last came to rest. Moves
 * through the individual `translate` property, the one geometry property no
 * positioning loop (`top`/`left`/`transform`), entrance animation or drag
 * gesture (`transform`) writes, so it composes with all of them. A glide still
 * running is cancelled and the new one starts from where `el` is drawn at that
 * instant, so a second change inside 350 ms never jumps by the leftover. Under
 * reduced motion the move lands in a single frame: the position is functional,
 * only the animation goes.
 */
export function glideY(el: HTMLElement, fromPx: number, toPx: number): void {
	const restY = parseFloat(el.style.translate.split(' ')[1] ?? '0') || 0
	const drawnY = parseFloat(getComputedStyle(el).translate.split(' ')[1] ?? '0') || 0
	for (const running of el.getAnimations()) if (running.id === GLIDE_ID) running.cancel()
	const startY = fromPx + drawnY - restY
	el.style.translate = toPx === 0 ? '' : `0 ${toPx}px`
	if (startY === toPx || reducedMotion$.value) return
	el.animate([{ translate: `0 ${startY}px` }, { translate: `0 ${toPx}px` }], {
		id: GLIDE_ID,
		duration: DURATION_EMPHASIZED_DECELERATE,
		easing: EASE_EMPHASIZED_DECELERATE,
	})
}
